import React, { FC } from 'react';
import { Modal, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as PaymentFailedContainerSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/payment-failed/container.svg';
import { ReactComponent as PaymentFailedVectorSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/payment-failed/vector.svg';
import './SubscriptionModals.scss';

interface PaymentFailedModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTryAgain?: () => void;
  onChangePaymentMethod?: () => void;
  errorCode?: string;
  subCode?: string;
  errorTitle?: string;
  errorMessage?: string;
  isLoading?: boolean;
}

export const PaymentFailedModal: FC<PaymentFailedModalProps> = ({
  isOpen,
  onClose,
  onTryAgain,
  onChangePaymentMethod,
  errorCode,
  subCode,
  errorTitle,
  errorMessage,
  isLoading = false,
}) => {
  const navigate = useNavigate();

  const handleBackToPlans = () => {
    onClose();
    navigate('/onboarding-trainer/dashboard/subscription/plans');
  };

  // Prioritize backend-provided errorTitle and human-friendly user message,
  // with fallback logic if not provided by backend.
  const getFailureDetails = () => {
    const rawMsg = errorMessage || '';
    const code = (errorCode || '').toUpperCase();
    const sub = (subCode || '').toUpperCase();

    if (errorTitle) {
      return {
        title: errorTitle,
        description:
          rawMsg ||
          'We were unable to complete this transaction. Please try again or use another payment method.',
      };
    }

    if (
      code === 'ERR_INSUFFICIENT_FUNDS' ||
      sub === 'PMT-001' ||
      rawMsg.toLowerCase().includes('insufficient funds')
    ) {
      return {
        title: 'Insufficient Funds',
        description:
          rawMsg ||
          'Your account has insufficient funds to complete this transaction. Please add funds to your account or try another payment card.',
      };
    }

    if (
      code === 'ERR_INVALID_CARD_DETAILS' ||
      sub === 'PMT-002' ||
      rawMsg.toLowerCase().includes('cvc') ||
      rawMsg.toLowerCase().includes('security code') ||
      rawMsg.toLowerCase().includes('expired')
    ) {
      return {
        title: 'Card Details Error',
        description:
          rawMsg ||
          'The expiration date, security code (CVC), or card number is invalid. Please double-check your card details.',
      };
    }

    if (
      code === 'ERR_3DS_AUTHENTICATION_FAILED' ||
      sub === 'PMT-004' ||
      rawMsg.toLowerCase().includes('authentication')
    ) {
      return {
        title: 'Authentication Incomplete',
        description:
          rawMsg ||
          'Your bank could not verify the payment authentication. Please ensure you approve the confirmation request in your banking app.',
      };
    }

    if (
      code === 'ERR_CARD_DECLINED' ||
      sub === 'PMT-003' ||
      rawMsg.toLowerCase().includes('declined')
    ) {
      return {
        title: 'Card Declined by Bank',
        description:
          rawMsg ||
          'Your bank declined this transaction. Please authorize online payments with your card issuer or try a different card.',
      };
    }

    return {
      title: 'Payment Could Not Be Completed',
      description:
        rawMsg ||
        'We were unable to complete this transaction. Please try again or use another payment method.',
    };
  };

  const { title: displayTitle, description: displayDescription } = getFailureDetails();

  return (
    <Modal
      open={isOpen}
      onCancel={() => {
        if (!isLoading) onClose();
      }}
      footer={null}
      centered
      width={480}
      closeIcon={null}
      maskClosable={!isLoading}
      keyboard={!isLoading}
      rootClassName="subscription-modal-wrapper"
      styles={{
        mask: {
          backdropFilter: 'blur(8px)',
          backgroundColor: 'rgba(6, 7, 35, 0.75)',
        },
      }}
    >
      <div className="result-modal-content payment-failed-content">
        <div className="result-icon-wrapper">
          <PaymentFailedContainerSVG />
        </div>

        <h2>{'Payment Failed'}</h2>
        <p className="result-subtitle">
          {"We couldn't process your payment. Please check your payment method or try again."}
        </p>

        {/* Clear human-friendly error details box */}
        <div className="error-code-box">
          <div className="error-header">
            <PaymentFailedVectorSVG />
            <span>{displayTitle}</span>
          </div>
          <p className="error-msg">{displayDescription}</p>
        </div>

        <div className="result-actions column-layout">
          <button
            type="button"
            className="btn-primary-action"
            disabled={isLoading}
            onClick={() => {
              if (onTryAgain) onTryAgain();
              else onClose();
            }}
          >
            {isLoading ? (
              <span className="btn-loading-content">
                <Spin
                  indicator={<LoadingOutlined style={{ fontSize: 16, color: '#060723' }} spin />}
                />
                <span>{'Retrying Payment...'}</span>
              </span>
            ) : (
              'Try Again'
            )}
          </button>
          <button
            type="button"
            className="btn-secondary-action"
            disabled={isLoading}
            onClick={() => {
              onClose();
              if (onChangePaymentMethod) onChangePaymentMethod();
            }}
          >
            {'Change Payment Method'}
          </button>
          <button
            type="button"
            className="btn-ghost-action"
            disabled={isLoading}
            onClick={handleBackToPlans}
          >
            {'Back to Plans'}
          </button>
        </div>
      </div>
    </Modal>
  );
};
